import { AxiosInstance } from "axios";
import { OrganizationPublicId } from "../Types";
import {
  AbandonDataProtectionAssessmentRequest,
  AddDPAAnswerRequest,
  AddDPAApprovalPartyRequest,
  CreateDataProtectionAssessmentRequest,
  DataProtectionAssessmentDto,
  DPAAnswerDto,
  DPAApprovalPartyDto,
  DPAPromptDto,
  DPAType,
  UpdateDataProtectionAssessmentRequest,
  UpdateDPAApprovalPartyRequest,
} from "../dto/DataProtectionAssessmentDto";
import { ApiRouteHelpers } from "../../../root/apiRoutes";

export class DataProtectionAssessmentController {
  constructor(private axios: AxiosInstance) {}

  public getDpas = async (
    organizationId: OrganizationPublicId,
  ): Promise<DataProtectionAssessmentDto[]> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.root(organizationId);
    const response = await this.axios.get<DataProtectionAssessmentDto[]>(url);
    return response.data as DataProtectionAssessmentDto[];
  };

  public getDpa = async (
    organizationId: OrganizationPublicId,
    dpaId: string,
  ): Promise<DataProtectionAssessmentDto> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.specific(
      organizationId,
      dpaId,
    );
    const response = await this.axios.get<DataProtectionAssessmentDto>(url);
    return response.data as DataProtectionAssessmentDto;
  };

  public getSuggestedDpas = async (
    organizationId: OrganizationPublicId,
  ): Promise<Record<DPAType, boolean>> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.suggested(organizationId);
    const response = await this.axios.get<Record<DPAType, boolean>>(url);
    return response.data as Record<DPAType, boolean>;
  };

  public createDpa = async (
    organizationId: OrganizationPublicId,
    createDpaRequest: CreateDataProtectionAssessmentRequest,
  ): Promise<DataProtectionAssessmentDto> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.root(organizationId);
    const response = await this.axios.post<DataProtectionAssessmentDto>(url, createDpaRequest);
    return response.data as DataProtectionAssessmentDto;
  };

  public updateDpa = async (
    organizationId: OrganizationPublicId,
    dpaId: string,
    updateDpaRequest: UpdateDataProtectionAssessmentRequest,
  ): Promise<DataProtectionAssessmentDto> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.specific(
      organizationId,
      dpaId,
    );
    const response = await this.axios.put<DataProtectionAssessmentDto>(url, updateDpaRequest);
    return response.data as DataProtectionAssessmentDto;
  };

  public abandonDpa = async (
    organizationId: OrganizationPublicId,
    dpaId: string,
    abandonRequest: AbandonDataProtectionAssessmentRequest,
  ): Promise<void> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.abandon(
      organizationId,
      dpaId,
    );
    await this.axios.post<AbandonDataProtectionAssessmentRequest>(url, abandonRequest);
  };

  public uploadUnsignedPdf = async (
    organizationId: OrganizationPublicId,
    dpaId: string,
    file: File,
  ): Promise<void> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.uploadUnsignedPdf(
      organizationId,
      dpaId,
    );
    const formData = new FormData();
    formData.append("file", file, file.name);
    await this.axios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  public uploadPreSignedPdf = async (
    organizationId: OrganizationPublicId,
    dpaId: string,
    file: File,
  ): Promise<DataProtectionAssessmentDto> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.uploadPreSignedPdf(
      organizationId,
      dpaId,
    );
    const formData = new FormData();
    formData.append("file", file, file.name);
    const response = await this.axios.post<DataProtectionAssessmentDto>(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data as DataProtectionAssessmentDto;
  };

  public getDpaPrompts = async (organizationId: OrganizationPublicId): Promise<DPAPromptDto[]> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.prompts(organizationId);
    const response = await this.axios.get<DPAPromptDto[]>(url);
    return response.data as DPAPromptDto[];
  };

  public getDpaAnswers = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
  ): Promise<DPAAnswerDto[]> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.answers.root(
      organizationId,
      dpaId,
    );
    const response = await this.axios.get<DPAAnswerDto[]>(url);
    return response.data as DPAAnswerDto[];
  };

  public addDpaAnswer = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
    addAnswerRequest: AddDPAAnswerRequest,
  ): Promise<void> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.answers.root(
      organizationId,
      dpaId,
    );
    await this.axios.post(url, addAnswerRequest);
  };

  public getDpaApprovalParties = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
  ): Promise<DPAApprovalPartyDto[]> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.approvalParties.root(
        organizationId,
        dpaId,
      );
    const response = await this.axios.get<DPAApprovalPartyDto[]>(url);
    return response.data as DPAApprovalPartyDto[];
  };

  public addApprovalParty = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
    addPartyRequest: AddDPAApprovalPartyRequest,
  ): Promise<DPAApprovalPartyDto> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.approvalParties.root(
        organizationId,
        dpaId,
      );
    const response = await this.axios.post<DPAApprovalPartyDto>(url, addPartyRequest);
    return response.data as DPAApprovalPartyDto;
  };

  public finalizeSignatures = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
  ): Promise<void> => {
    const url = ApiRouteHelpers.organization.documents.dataProtectionAssessments.finalizeSignatures(
      organizationId,
      dpaId,
    );
    await this.axios.post(url);
  };

  public cancelSignatureRequest = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
  ): Promise<void> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.cancelSignatureRequest(
        organizationId,
        dpaId,
      );
    await this.axios.post(url);
  };

  public updateDpaApprovalParty = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
    partyId: String,
    updatePartyRequest: UpdateDPAApprovalPartyRequest,
  ): Promise<DPAApprovalPartyDto> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.approvalParties.specific(
        organizationId,
        dpaId,
        partyId,
      );
    const response = await this.axios.put<DPAApprovalPartyDto>(url, updatePartyRequest);
    return response.data as DPAApprovalPartyDto;
  };

  public removeDpaApprovalParty = async (
    organizationId: OrganizationPublicId,
    dpaId: String,
    partyId: String,
  ): Promise<void> => {
    const url =
      ApiRouteHelpers.organization.documents.dataProtectionAssessments.approvalParties.specific(
        organizationId,
        dpaId,
        partyId,
      );
    return this.axios.delete(url);
  };
}
